import { Inject, Injectable, InjectionToken } from '@angular/core';
import { RoiStore, RoiChangeStore } from '../../stores';
import { EMPTY, Observable, from } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ListAssetRoisUseCase } from '@analysis/use-cases';
import { concatLatestFrom } from '@ngrx/operators';
import { ProtocolStore } from '@telespot/protocols/ui';
import { LabelingService, LabelOptions, Roi, RoiFinding, RoiFindingMapper } from '@analysis/domain';

export const LIST_ASSET_ROIS_USE_CASE = new InjectionToken<ListAssetRoisUseCase>('LIST_ASSET_ROIS_USE_CASE');

@Injectable()
export class MicroRoiFeatureStore {
  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    @Inject(LIST_ASSET_ROIS_USE_CASE) private readonly listAssetRoisUseCase: ListAssetRoisUseCase,
    private readonly protocolStore: ProtocolStore
  ) {}

  public readonly loadAssetRois = this.roiStore.effect(
    (
      action$: Observable<{
        assetId: string;
        sampleId: string;
        analystId: string;
        analystEntity: 'user' | 'algorithm';
      }>
    ) =>
      action$.pipe(
        switchMap(({ assetId, sampleId, analystId, analystEntity }) => {
          this.roiStore.setLoading(true);

          return from(
            this.listAssetRoisUseCase.execute({
              assetId,
              sampleId,
              creatorId: analystId,
              creatorEntity: analystEntity,
            })
          ).pipe(
            concatLatestFrom(() => [
              this.roiChangeStore.changedRois$,
              this.protocolStore.options$,
              this.roiStore.rois$,
            ]),
            tap(([{ items, roiFindings: emptyFindings }, changedRois, options, roiEntities]) => {
              const prepared = this.prepareAssetRois(
                items,
                emptyFindings ?? [],
                changedRois,
                roiEntities,
                assetId,
                analystId,
                analystEntity,
                options ?? {}
              );
              this.roiStore.loadAssetRoisResult(prepared);
            }),
            catchError((error) => {
              this.roiStore.setLoadError(error.message);
              return EMPTY;
            })
          );
        })
      )
  );

  private prepareAssetRois(
    roisWithChanges: Roi[],
    emptyFindings: RoiFinding[],
    changedRois: Map<string, unknown>,
    roiEntities: Record<string, Roi>,
    assetId: string,
    analystId: string,
    analystEntity: 'user' | 'algorithm',
    options: LabelOptions
  ): { rois: Roi[]; roiFindings: Map<string, RoiFinding> } {
    const changedRoiIds = new Set(changedRois.keys());

    const changedOnThisAsset = this.findChangedRoisOnAsset(
      roisWithChanges,
      changedRoiIds,
      roiEntities,
      assetId,
      analystId,
      analystEntity
    );

    const allRoisForAsset = [...roisWithChanges, ...changedOnThisAsset];
    const filteredRoisForAsset = allRoisForAsset.map((roi) => LabelingService.filterLabels(roi, options));

    const changedOnOtherAssets = this.findChangedRoisOnOtherAssets(changedRoiIds, roiEntities, assetId);
    const combinedRois = [...filteredRoisForAsset, ...changedOnOtherAssets];

    const roiFindingsFromRois = RoiFindingMapper.fromRois(filteredRoisForAsset);
    const roiFindingsFromApi = RoiFindingMapper.fromList(emptyFindings);
    const roiFindings = RoiFindingMapper.merge(roiFindingsFromApi, roiFindingsFromRois);

    return { rois: combinedRois, roiFindings };
  }

  private findChangedRoisOnAsset(
    apiRois: Roi[],
    changedRoiIds: Set<string>,
    roiEntities: Record<string, Roi>,
    assetId: string,
    analystId: string,
    analystEntity: 'user' | 'algorithm'
  ): Roi[] {
    const itemIds = new Set(apiRois.map((roi) => roi.id));
    const result: Roi[] = [];

    for (const roiId of changedRoiIds) {
      const roiInState = roiEntities[roiId];
      if (
        !roiInState ||
        roiInState.assetId !== assetId ||
        itemIds.has(roiId) ||
        roiInState.creatorId !== analystId ||
        roiInState.creatorEntity !== analystEntity
      ) {
        continue;
      }
      result.push(roiInState);
    }

    return result;
  }

  private findChangedRoisOnOtherAssets(
    changedRoiIds: Set<string>,
    roiEntities: Record<string, Roi>,
    assetId: string
  ): Roi[] {
    const result: Roi[] = [];
    for (const roiId of changedRoiIds) {
      const roi = roiEntities[roiId];
      if (roi && roi.assetId !== assetId) result.push(roi);
    }
    return result;
  }
}
